import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Plus,
  Layers,
  Film as FilmIcon,
  GripVertical,
  MoreHorizontal,
  SlidersHorizontal,
  Lock,
  Star,
  ChevronsUpDown,
  ChevronUp,
  Check,
  ArrowDownAZ,
  Clock,
  CalendarClock,
  CalendarDays,
  LayoutGrid,
  Copy as CopyIcon,
  ArrowRightLeft,
  X,
  ChevronLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { useFilm } from '../context/FilmContext';
import { useFeatures } from '../context/FeatureContext';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../hooks/queries';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '../components/ui/dropdown-menu';
import { API_BASE } from '../config/api';
import { cn } from '../lib/utils';
import ScheduleBoard from '../components/ScheduleBoard';

const API = API_BASE;

/* --------------------------- helpers / config --------------------------- */

// Status taxonomy:
//   not_ready  → NOT READY column
//   ready      → READY column
//   shot       → SHOT column (in progress / shot-but-not-yet-covered)
//   done       → SHOT column (visual: shows a 'DONE' pill instead of 'IN PROGRESS')
const COLUMNS = [
  {
    id: 'not_ready',
    label: 'Not Ready',
    statuses: ['not_ready'],
    pillClass: 'bg-destructive/15 text-destructive',
    stripeClass: 'bg-destructive',
  },
  {
    id: 'ready',
    label: 'Ready',
    statuses: ['ready'],
    pillClass: 'bg-success/15 text-success',
    stripeClass: 'bg-success',
  },
  {
    id: 'shot',
    label: 'Shot',
    statuses: ['shot', 'done'],
    pillClass: 'bg-warning/15 text-warning',
    stripeClass: 'bg-warning',
  },
];

const padCount = (n) => String(n).padStart(2, '0');
const titleInitial = (s = '') => (s.trim() ? s.trim()[0].toUpperCase() : '?');

const getDestinationStatus = (columnId, currentStatus) => {
  const col = COLUMNS.find((c) => c.id === columnId);
  if (col && Array.isArray(col.statuses) && col.statuses.includes(currentStatus)) {
    return currentStatus;
  }
  return (col && col.statuses && col.statuses[0]) || columnId;
};

/* ------------------------------ Filters ------------------------------ */

const INT_EXT_OPTIONS = [
  { value: 'all',      label: 'All' },
  { value: 'interior', label: 'INT' },
  { value: 'exterior', label: 'EXT' },
];

const TIME_OF_DAY_OPTIONS = [
  { value: 'all',       label: 'All' },
  { value: 'day',       label: 'Day' },
  { value: 'morning',   label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening',   label: 'Evening' },
  { value: 'night',     label: 'Night' },
];

const DUE_WINDOW_OPTIONS = [
  { value: 'all',      label: 'All' },
  { value: 'overdue',  label: 'Overdue' },
  { value: 'week',     label: 'Due this week' },
  { value: 'month',    label: 'Due this month' },
  { value: 'no_due',   label: 'No due date' },
];

const SORT_OPTIONS = [
  { value: 'newest',     label: 'Newest first',  icon: Clock },
  { value: 'oldest',     label: 'Oldest first',  icon: Clock },
  { value: 'due_date',   label: 'By due date',   icon: CalendarClock },
  { value: 'scene_no',   label: 'By scene #',    icon: ArrowDownAZ },
];

const defaultFilters = () => ({
  intExt: 'all',
  timeOfDay: 'all',
  location: 'all',
  dueWindow: 'all',
});

const isWithinDueWindow = (iso, window) => {
  if (window === 'all') return true;
  if (window === 'no_due') return !iso;
  if (!iso) return false;
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (window === 'overdue') return due < startOfToday;
  if (window === 'week') {
    const end = new Date(startOfToday); end.setDate(end.getDate() + 7);
    return due >= startOfToday && due <= end;
  }
  if (window === 'month') {
    const end = new Date(startOfToday); end.setMonth(end.getMonth() + 1);
    return due >= startOfToday && due <= end;
  }
  return true;
};

const countActiveFilters = (f) => {
  let n = 0;
  if (f.intExt !== 'all')    n += 1;
  if (f.timeOfDay !== 'all') n += 1;
  if (f.location !== 'all')  n += 1;
  if (f.dueWindow !== 'all') n += 1;
  return n;
};

/* ------------------------------ Scene Card ------------------------------ */

const SceneCard = ({
  scene,
  columnId,
  onClick,
  onDragStart,
  onDragEnd,
  isDragging,
}) => {
  const stripe = COLUMNS.find((c) => c.id === columnId)?.stripeClass || 'bg-muted';
  const sceneNumber = scene.scene_number || '—';
  const heading = scene.scene_name || scene.description || `Scene ${sceneNumber}`;

  const shots = Number(scene.shot_count) || 0;
  const covered = Number(scene.covered_shot_count) || 0;
  const coveragePct = shots > 0 ? Math.round((covered / shots) * 100) : 0;

  const overlayPill =
    columnId === 'shot'
      ? scene.status === 'done'
        ? { label: 'Done', cls: 'bg-success text-success-foreground' }
        : coveragePct < 100
          ? { label: 'In Progress', cls: 'bg-warning text-warning-foreground' }
          : null
      : null;

  const isGolden = scene.is_golden === true;

  return (
    <article
      draggable
      onDragStart={(e) => onDragStart(e, scene)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`scene-card-${scene.id}`}
      className={cn(
        'group relative rounded-xl bg-card border border-border overflow-hidden cursor-pointer transition-all',
        'hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-md',
        isDragging && 'opacity-40 scale-[0.98]'
      )}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', stripe)} />

      <div className="pl-4 pr-3 py-2.5 flex items-center gap-3">
        {/* Scene number "thumb" — replaces the old aspect-video block */}
        <div
          aria-hidden
          className={cn(
            'shrink-0 size-11 rounded-lg flex items-center justify-center',
            'bg-gradient-to-br from-primary/20 via-primary/10 to-warning/15',
            'border border-border/60'
          )}
        >
          <span className="font-display font-bold text-base text-foreground/70 tabular-nums leading-none">
            {padCount(sceneNumber === '—' ? 0 : (parseInt(String(sceneNumber).match(/^\d+/)?.[0], 10) || 0))}
          </span>
        </div>

        {/* Title + meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-primary">
              Scene {sceneNumber}
            </span>
            {isGolden && (
              <span
                aria-label="Featured scene"
                className="inline-flex items-center justify-center size-3.5 rounded-full bg-warning shrink-0"
              >
                <Star className="size-2.5 text-warning-foreground fill-current" />
              </span>
            )}
            {overlayPill && (
              <span
                className={cn(
                  'inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold tracking-[0.15em] uppercase',
                  overlayPill.cls
                )}
              >
                {overlayPill.label}
              </span>
            )}
          </div>
          <h3 className="text-sm font-semibold leading-snug line-clamp-1 tracking-tight">
            {heading}
          </h3>
          <div className="flex items-center gap-2.5 mt-1 text-muted-foreground">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium tabular-nums">
              <FilmIcon className="size-3" />
              {padCount(shots)} shots
            </span>
            <span className="text-muted-foreground/30 text-[10px]">·</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium tabular-nums">
              <Layers className="size-3" />
              {coveragePct}%
            </span>
          </div>
        </div>

        <GripVertical
          className="size-4 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab active:cursor-grabbing shrink-0"
          aria-hidden
        />
      </div>
    </article>
  );
};

/* --------------------------- FilterPopover --------------------------- */

const ChipRow = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        className={cn(
          'text-[11px] font-semibold tracking-wide rounded-full px-2.5 py-1 border transition-colors',
          value === opt.value
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground'
        )}
        data-testid={`${testId}-${opt.value}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const FilterPopover = ({ filters, onChange, onClear, locationOptions, activeCount }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className="rounded-xl gap-2 h-10"
        data-testid="sb-filter"
      >
        <SlidersHorizontal className="size-4" />
        Filter
        {activeCount > 0 && (
          <span
            className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold"
            data-testid="sb-filter-active-count"
          >
            {activeCount}
          </span>
        )}
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-80 p-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <p className="text-sm font-semibold tracking-tight">Filter scenes</p>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] font-semibold tracking-wide uppercase text-primary hover:text-primary/80"
            data-testid="sb-filter-clear"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="px-4 py-3 space-y-4">
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
            Int / Ext
          </p>
          <ChipRow
            options={INT_EXT_OPTIONS}
            value={filters.intExt}
            onChange={(v) => onChange({ ...filters, intExt: v })}
            testId="sb-filter-intext"
          />
        </div>
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
            Time of Day
          </p>
          <ChipRow
            options={TIME_OF_DAY_OPTIONS}
            value={filters.timeOfDay}
            onChange={(v) => onChange({ ...filters, timeOfDay: v })}
            testId="sb-filter-tod"
          />
        </div>
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
            Location
          </p>
          <Select
            value={filters.location}
            onValueChange={(v) => onChange({ ...filters, location: v })}
          >
            <SelectTrigger className="h-9 text-sm" data-testid="sb-filter-location">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {locationOptions.map((loc) => (
                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
              ))}
              {locationOptions.length === 0 && (
                <SelectItem value="__none" disabled>No locations on any scene yet</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
            Due window
          </p>
          <ChipRow
            options={DUE_WINDOW_OPTIONS}
            value={filters.dueWindow}
            onChange={(v) => onChange({ ...filters, dueWindow: v })}
            testId="sb-filter-due"
          />
        </div>
      </div>
    </PopoverContent>
  </Popover>
);

/* ------------------------------ Column ------------------------------ */

const ColumnActionsMenu = ({
  column,
  collapsed,
  sortBy,
  scenes,
  canManage,
  onToggleCollapse,
  onSortChange,
  onCopyList,
  onBulkMoveTo,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="size-7 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          aria-label="Column actions"
          data-testid={`sb-col-menu-${column.id}`}
        >
          <MoreHorizontal className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); onToggleCollapse(); }}
          data-testid={`sb-col-toggle-${column.id}`}
        >
          {collapsed
            ? (<><ChevronsUpDown className="size-4 mr-2" /> Expand column</>)
            : (<><ChevronUp     className="size-4 mr-2" /> Collapse column</>)}
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); onCopyList(); }}
          disabled={scenes.length === 0}
          data-testid={`sb-col-copy-${column.id}`}
        >
          <CopyIcon className="size-4 mr-2" /> Copy scene list
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
          Sort within column
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={sortBy}
          onValueChange={onSortChange}
        >
          {SORT_OPTIONS.map((opt) => (
            <DropdownMenuRadioItem
              key={opt.value}
              value={opt.value}
              data-testid={`sb-col-sort-${column.id}-${opt.value}`}
            >
              <opt.icon className="size-4 mr-2 text-muted-foreground" />
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        {canManage && scenes.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              Move all
            </DropdownMenuLabel>
            {COLUMNS.filter((c) => c.id !== column.id).map((target) => (
              <DropdownMenuItem
                key={target.id}
                onSelect={(e) => { e.preventDefault(); onBulkMoveTo(target.id); }}
                data-testid={`sb-col-move-${column.id}-to-${target.id}`}
              >
                <ArrowRightLeft className="size-4 mr-2" /> Move all to {target.label}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Column = ({
  column,
  scenes,
  isDropTarget,
  collapsed,
  sortBy,
  canManage,
  onDragOver,
  onDragLeave,
  onDrop,
  onCardDragStart,
  onCardDragEnd,
  draggingId,
  onSceneClick,
  onToggleCollapse,
  onSortChange,
  onCopyList,
  onBulkMoveTo,
}) => (
  <section
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    onDrop={onDrop}
    data-testid={`column-${column.id}`}
    className={cn(
      'flex flex-col rounded-2xl transition-colors',
      isDropTarget && 'bg-primary/5 ring-1 ring-primary/30'
    )}
  >
    <header className="flex items-center justify-between px-2 pb-3">
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex items-center gap-2.5 -mx-1 px-1 py-0.5 rounded-md hover:bg-accent/60 transition-colors"
        data-testid={`sb-col-toggle-header-${column.id}`}
        title={collapsed ? 'Expand column' : 'Collapse column'}
      >
        <h2 className="text-xs font-semibold tracking-[0.22em] uppercase text-foreground">
          {column.label}
        </h2>
        <span
          className={cn(
            'inline-flex items-center px-2 min-w-[28px] h-[20px] justify-center rounded-full text-[11px] font-bold tabular-nums',
            column.pillClass
          )}
          data-testid={`column-count-${column.id}`}
        >
          {padCount(scenes.length)}
        </span>
        <ChevronUp
          className={cn(
            'size-3.5 text-muted-foreground/60 transition-transform',
            collapsed && 'rotate-180'
          )}
          aria-hidden
        />
      </button>
      <ColumnActionsMenu
        column={column}
        collapsed={collapsed}
        sortBy={sortBy}
        scenes={scenes}
        canManage={canManage}
        onToggleCollapse={onToggleCollapse}
        onSortChange={onSortChange}
        onCopyList={onCopyList}
        onBulkMoveTo={onBulkMoveTo}
      />
    </header>

    {collapsed ? (
      <div
        className="rounded-2xl border border-dashed border-border bg-card/30 py-4 text-center"
        data-testid={`sb-col-collapsed-${column.id}`}
      >
        <p className="text-[11px] text-muted-foreground">
          {scenes.length} {scenes.length === 1 ? 'scene' : 'scenes'} hidden — click header to expand.
        </p>
      </div>
    ) : (
      <div className="flex-1 space-y-4 min-h-[120px]">
        {scenes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/30 py-10 text-center">
            <Layers className="size-6 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No scenes here yet.</p>
          </div>
        ) : (
          scenes.map((scene) => (
            <SceneCard
              key={scene.id}
              scene={scene}
              columnId={column.id}
              onClick={() => onSceneClick(scene)}
              onDragStart={onCardDragStart}
              onDragEnd={onCardDragEnd}
              isDragging={draggingId === scene.id}
            />
          ))
        )}
      </div>
    )}
  </section>
);

/* ============================== Page ============================== */

const SceneBoard = () => {
  const { filmId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { fetchFilm, invalidateFilm } = useFilm();
  const { getEntityLimit, refreshFeatures } = useFeatures();
  const queryClient = useQueryClient();

  // [J] Bust the RQ film/scenes caches after a scene mutation (or before
  // navigating to Scene Detail) so the RQ-migrated Film Dashboard / Workspace
  // Home re-fetch fresh scene/shot counts on return.
  const bustFilmCaches = useCallback(() => {
    if (!filmId) return;
    queryClient.invalidateQueries({ queryKey: qk.film(filmId) });
    queryClient.invalidateQueries({ queryKey: qk.filmScenes(filmId) });
  }, [queryClient, filmId]);

  const [film, setFilm] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);

  // create-scene dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sceneNumber, setSceneNumber] = useState('');
  const [sceneName, setSceneName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('not_ready');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [creating, setCreating] = useState(false);

  // DnD state
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetCol, setDropTargetCol] = useState(null);

  // Filters
  const [filters, setFilters] = useState(defaultFilters());

  // Per-column UI prefs (collapsed, sort) — persisted per-film in localStorage.
  // Shape: { not_ready: { collapsed, sortBy }, ready: {...}, shot: {...} }
  const prefsKey = filmId ? `sb_prefs_${filmId}` : null;
  const [colPrefs, setColPrefs] = useState(() => {
    if (typeof window === 'undefined' || !prefsKey) {
      return { not_ready: { collapsed: false, sortBy: 'newest' }, ready: { collapsed: false, sortBy: 'newest' }, shot: { collapsed: false, sortBy: 'newest' } };
    }
    try {
      const raw = window.localStorage.getItem(prefsKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          not_ready: { collapsed: !!parsed?.not_ready?.collapsed, sortBy: parsed?.not_ready?.sortBy || 'newest' },
          ready:     { collapsed: !!parsed?.ready?.collapsed,     sortBy: parsed?.ready?.sortBy     || 'newest' },
          shot:      { collapsed: !!parsed?.shot?.collapsed,      sortBy: parsed?.shot?.sortBy      || 'newest' },
        };
      }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneBoard] corrupt board prefs, falling back:', err);
    }
    return { not_ready: { collapsed: false, sortBy: 'newest' }, ready: { collapsed: false, sortBy: 'newest' }, shot: { collapsed: false, sortBy: 'newest' } };
  });

  useEffect(() => {
    if (!prefsKey || typeof window === 'undefined') return;
    try { window.localStorage.setItem(prefsKey, JSON.stringify(colPrefs)); } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneBoard] localStorage write failed:', err);
    }
  }, [prefsKey, colPrefs]);

  // Bulk-move confirmation state
  const [bulkMove, setBulkMove] = useState(null); // { fromColId, toColId, count } | null
  const [bulkMoving, setBulkMoving] = useState(false);

  // Board view mode: 'status' (kanban) or 'schedule' (shooting schedules)
  const [viewMode, setViewMode] = useState(() => {
    try { return window.localStorage.getItem(`sb_view_${filmId}`) || 'status'; } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneBoard] viewMode read failed:', err);
      return 'status';
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(`sb_view_${filmId}`, viewMode); } catch (err) {
      if (import.meta.env.DEV) console.warn('[SceneBoard] viewMode write failed:', err);
    }
  }, [filmId, viewMode]);

  const sceneLimit = getEntityLimit('scenes_per_film');
  const scenesAtLimit = !sceneLimit.unlimited && scenes.length >= sceneLimit.limit;

  /* ----- data ----- */

  // [C] cancels the in-flight bundle on unmount / film change so a late
  // response (or a logout 401) can't clobber state or toast an error.
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const [filmData, scenesRes, membersRes] = await Promise.all([
        fetchFilm(filmId, true), // force-refresh so counts are not stale on re-entry
        axios.get(`${API}/films/${filmId}/scenes`,    { headers: getAuthHeaders(), signal: controller.signal }),
        axios.get(`${API}/films/${filmId}/members`,   { headers: getAuthHeaders(), signal: controller.signal }).catch(() => ({ data: [] })),
      ]);
      if (controller.signal.aborted) return;
      setFilm(filmData);
      setScenes(scenesRes.data || []);

      // Prefer backend-provided permission (single source of truth, includes
      // workspace-role-derived edit access). Fall back to legacy derivation
      // for older payloads.
      let editAccess;
      if (typeof filmData?.can_edit === 'boolean') {
        editAccess = filmData.can_edit;
      } else {
        const isOwner = filmData?.user_id === user?.id;
        const userMember = (membersRes.data || []).find((m) => m.user_id === user?.id);
        const hasEditAccess = userMember && ['admin', 'editor'].includes(userMember.access_type);
        editAccess = isOwner || hasEditAccess;
      }
      setCanManage(!!editAccess);
    } catch (err) {
      if (controller.signal.aborted || axios.isCancel?.(err)) return;
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load scenes');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [filmId, fetchFilm, user?.id]);

  useEffect(() => {
    fetchData();
    return () => { abortRef.current?.abort(); };
  }, [fetchData]);

  /* ----- derivations ----- */

  // Distinct location values across the film's scenes (for the filter select).
  const locationOptions = useMemo(() => {
    const set = new Set();
    for (const s of scenes) {
      const loc = (s.location || '').trim();
      if (loc) set.add(loc);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [scenes]);

  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);

  // Apply filters across all scenes BEFORE bucketing into columns so column
  // counts reflect the filtered view.
  const filteredScenes = useMemo(() => {
    return scenes.filter((s) => {
      if (filters.intExt    !== 'all' && s.interior_exterior !== filters.intExt)   return false;
      if (filters.timeOfDay !== 'all' && s.time_of_day       !== filters.timeOfDay) return false;
      if (filters.location  !== 'all') {
        const loc = (s.location || '').trim();
        if (loc !== filters.location) return false;
      }
      if (!isWithinDueWindow(s.due_date, filters.dueWindow)) return false;
      return true;
    });
  }, [scenes, filters]);

  const scenesByColumn = useMemo(() => {
    const map = { not_ready: [], ready: [], shot: [] };
    for (const s of filteredScenes) {
      const target = COLUMNS.find((c) => c.statuses.includes(s.status));
      if (target) map[target.id].push(s);
      else map.not_ready.push(s);
    }
    // Apply per-column sort
    const sceneNumKey = (s) => {
      // Try numeric prefix first so '10' sorts after '9'; fall back to localeCompare.
      const m = String(s.scene_number ?? '').match(/^(\d+)/);
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    for (const colId of Object.keys(map)) {
      const sortBy = colPrefs[colId]?.sortBy || 'newest';
      map[colId].sort((a, b) => {
        switch (sortBy) {
          case 'oldest':
            return new Date(a.created_at) - new Date(b.created_at);
          case 'due_date': {
            // Items without due_date sink to the bottom
            const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
            const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
            if (ad !== bd) return ad - bd;
            return new Date(b.created_at) - new Date(a.created_at);
          }
          case 'scene_no': {
            const an = sceneNumKey(a);
            const bn = sceneNumKey(b);
            if (an !== bn) return an - bn;
            return String(a.scene_number || '').localeCompare(String(b.scene_number || ''));
          }
          case 'newest':
          default:
            return new Date(b.created_at) - new Date(a.created_at);
        }
      });
    }
    return map;
  }, [filteredScenes, colPrefs]);

  /* ----- handlers ----- */

  const handleCreateScene = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        scene_number: sceneNumber,
        scene_name: sceneName,
        description,
        status,
      };
      if (startDate) payload.start_date = new Date(startDate).toISOString();
      if (dueDate)   payload.due_date   = new Date(dueDate).toISOString();

      await axios.post(`${API}/films/${filmId}/scenes`, payload, { headers: getAuthHeaders() });

      setSceneNumber(''); setSceneName(''); setDescription(''); setStatus('not_ready');
      setStartDate('');   setDueDate('');
      setDialogOpen(false);
      fetchData();
      bustFilmCaches();
      refreshFeatures();
      toast.success('Scene created successfully');
    } catch (error) {
      console.error('Failed to create scene:', error);
      const message = error.response?.data?.detail || 'Failed to create scene';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = useCallback(async (sceneId, newStatus) => {
    setScenes((prev) =>
      prev.map((s) => (s.id === sceneId ? { ...s, status: newStatus } : s))
    );
    try {
      await axios.put(`${API}/scenes/${sceneId}`, { status: newStatus }, { headers: getAuthHeaders() });
      bustFilmCaches();
    } catch (err) {
      console.error('Failed to update scene status:', err);
      toast.error('Could not update scene status');
      fetchData();
    }
  }, [fetchData, bustFilmCaches]);

  /* ----- DnD wiring (native HTML5) ----- */

  const onCardDragStart = (e, scene) => {
    if (!canManage) {
      try { e.preventDefault(); } catch (err) { if (import.meta.env.DEV) console.debug('[SceneBoard] dragStart preventDefault failed:', err); }
      return;
    }
    setDraggingId(scene.id);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', scene.id);
    } catch (err) { if (import.meta.env.DEV) console.debug('[SceneBoard] dataTransfer.setData failed:', err); }
  };
  const onCardDragEnd = () => {
    setDraggingId(null);
    setDropTargetCol(null);
  };
  const onColumnDragOver = (columnId) => (e) => {
    if (!draggingId) return;
    e.preventDefault();
    if (dropTargetCol !== columnId) setDropTargetCol(columnId);
    try { e.dataTransfer.dropEffect = 'move'; } catch (err) { if (import.meta.env.DEV) console.debug('[SceneBoard] dropEffect set failed:', err); }
  };
  const onColumnDragLeave = (columnId) => () => {
    if (dropTargetCol === columnId) setDropTargetCol(null);
  };
  const onColumnDrop = (columnId) => (e) => {
    e.preventDefault();
    const sceneId = draggingId || e.dataTransfer?.getData('text/plain');
    setDraggingId(null);
    setDropTargetCol(null);
    if (!sceneId) return;
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    const newStatus = getDestinationStatus(columnId, scene.status);
    if (scene.status === newStatus) return;
    handleStatusChange(sceneId, newStatus);
  };

  const onSceneClick = (scene) => {
    if (draggingId) return;
    // Invalidate the film cache so that, when the user comes back from
    // Scene Detail, the Film Dashboard re-fetches and shows updated
    // shot / coverage counts instead of stale numbers.
    try { invalidateFilm && invalidateFilm(filmId); } catch (err) { console.warn('[SceneBoard] invalidateFilm failed:', err); }
    bustFilmCaches();
    navigate(`/scenes/${scene.id}`);
  };

  /* ----- column actions ----- */

  const toggleColumnCollapse = (colId) => {
    setColPrefs((prev) => ({
      ...prev,
      [colId]: { ...prev[colId], collapsed: !prev[colId]?.collapsed },
    }));
  };

  const setColumnSort = (colId) => (sortBy) => {
    setColPrefs((prev) => ({
      ...prev,
      [colId]: { ...prev[colId], sortBy },
    }));
  };

  const copyColumnList = (colId) => async () => {
    const items = scenesByColumn[colId] || [];
    if (items.length === 0) {
      toast.info('Nothing to copy — column is empty.');
      return;
    }
    const lines = items.map((s) => {
      const heading = s.scene_name || s.description || `Scene ${s.scene_number}`;
      const shots = Number(s.shot_count) || 0;
      const covered = Number(s.covered_shot_count) || 0;
      const cov = shots > 0 ? Math.round((covered / shots) * 100) : 0;
      return `- Scene ${s.scene_number || '—'} — ${heading} (${covered}/${shots} covered, ${cov}%)`;
    });
    const colLabel = COLUMNS.find((c) => c.id === colId)?.label || colId;
    const markdown = `### ${colLabel} (${items.length})\n${lines.join('\n')}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(markdown);
      } else {
        // Fallback for non-HTTPS / older browsers
        const ta = document.createElement('textarea');
        ta.value = markdown;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${items.length} ${items.length === 1 ? 'scene' : 'scenes'} to clipboard`);
    } catch (err) {
      console.error('Clipboard write failed:', err);
      toast.error('Could not copy to clipboard');
    }
  };

  const beginBulkMove = (fromColId) => (toColId) => {
    const items = scenesByColumn[fromColId] || [];
    if (items.length === 0) return;
    setBulkMove({ fromColId, toColId, count: items.length });
  };

  const confirmBulkMove = async () => {
    if (!bulkMove) return;
    const { fromColId, toColId } = bulkMove;
    const items = scenesByColumn[fromColId] || [];
    if (items.length === 0) { setBulkMove(null); return; }
    const targetStatus = COLUMNS.find((c) => c.id === toColId)?.statuses?.[0] || toColId;

    setBulkMoving(true);
    // Optimistic update
    const ids = items.map((s) => s.id);
    setScenes((prev) => prev.map((s) => (ids.includes(s.id) ? { ...s, status: targetStatus } : s)));
    try {
      await Promise.all(
        ids.map((id) =>
          axios.put(`${API}/scenes/${id}`, { status: targetStatus }, { headers: getAuthHeaders() })
        )
      );
      bustFilmCaches();
      const colLabel = COLUMNS.find((c) => c.id === toColId)?.label || toColId;
      toast.success(`Moved ${ids.length} ${ids.length === 1 ? 'scene' : 'scenes'} to ${colLabel}`);
    } catch (err) {
      console.error('Bulk move failed:', err);
      toast.error('Some scenes failed to move — refreshing.');
      fetchData();
    } finally {
      setBulkMoving(false);
      setBulkMove(null);
    }
  };

  /* ----- render ----- */

  if (loading) {
    return (
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="scene-board-loading">
        <Skeleton className="h-4 w-40 mb-3" />
        <Skeleton className="h-10 w-64 mb-10" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((c) => (
            <div key={c} className="space-y-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-[260px] rounded-2xl" />
              <Skeleton className="h-[260px] rounded-2xl" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasAnyScenes = scenes.length > 0;
  const totalFiltered = filteredScenes.length;
  const filtersActive = activeFilterCount > 0;

  return (
    <div className="bg-background flex-1 min-w-0" data-testid="scene-board">
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Back-link to Film Dashboard */}
        {film && (
          <nav className="mb-4" aria-label="Breadcrumb">
            <button
              type="button"
              onClick={() => {
                const wsId = film.workspace_id;
                const target = wsId
                  ? `/workspaces/${wsId}/films/${filmId}`
                  : `/films/${filmId}`;
                navigate(target);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground hover:text-foreground transition-colors -ml-1 px-1 py-0.5 rounded-md hover:bg-accent/60"
              data-testid="sb-back-to-film"
            >
              <ChevronLeft className="size-3.5" />
              <span className="truncate max-w-[260px] md:max-w-[400px]">
                Back to {film.title || 'Film'}
              </span>
            </button>
          </nav>
        )}

        {/* Header band */}
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="min-w-0">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-primary mb-2">
              Production Stage
            </p>
            <h1
              className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05]"
              data-testid="sb-hero-title"
            >
              Scene Board
              {film?.title && (
                <span className="block md:inline md:ml-3 text-base md:text-2xl text-muted-foreground font-normal align-middle truncate">
                  · {film.title}
                </span>
              )}
            </h1>
          </div>

          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* View toggle: Status Board ↔ Schedule Board */}
            <div className="inline-flex items-center rounded-xl border border-border bg-card p-0.5 h-10">
              <button
                type="button"
                onClick={() => setViewMode('status')}
                aria-pressed={viewMode === 'status'}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold transition-all',
                  viewMode === 'status'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                data-testid="sb-view-status"
              >
                <LayoutGrid className="size-3.5" />
                <span className="hidden sm:inline">Status</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('schedule')}
                aria-pressed={viewMode === 'schedule'}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-xs font-semibold transition-all',
                  viewMode === 'schedule'
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                data-testid="sb-view-schedule"
              >
                <CalendarDays className="size-3.5" />
                <span className="hidden sm:inline">Schedules</span>
              </button>
            </div>

            {viewMode === 'status' && (
              <FilterPopover
              filters={filters}
              onChange={setFilters}
              onClear={() => setFilters(defaultFilters())}
              locationOptions={locationOptions}
              activeCount={activeFilterCount}
            />
            )}

            {!canManage ? null : scenesAtLimit ? (
              <Button size="sm" variant="outline" disabled className="rounded-xl gap-2 h-10">
                <Lock className="size-4" />
                <span>Limit ({sceneLimit.limit})</span>
              </Button>
            ) : (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-scene-button" size="sm" className="rounded-xl gap-2 h-10">
                    <Plus className="size-4" />
                    <span>Add Scene</span>
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="create-scene-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New Scene</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateScene} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sceneNumber">Scene Number</Label>
                      <Input
                        id="sceneNumber"
                        data-testid="scene-number-input"
                        placeholder="e.g., 1, 1A, 23"
                        value={sceneNumber}
                        onChange={(e) => setSceneNumber(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sceneName">Scene Name/Title (Optional)</Label>
                      <Input
                        id="sceneName"
                        data-testid="scene-name-input"
                        placeholder="e.g., Park Chase Scene, Office Confrontation"
                        value={sceneName}
                        onChange={(e) => setSceneName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        data-testid="scene-description-input"
                        placeholder="Detailed description of what happens in the scene"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="startDate">Start Date (Optional)</Label>
                        <Input
                          id="startDate"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          data-testid="scene-start-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dueDate">Due Date (Optional)</Label>
                        <Input
                          id="dueDate"
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          data-testid="scene-due-date-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Initial Status</Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid="scene-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_ready">Not Ready</SelectItem>
                          <SelectItem value="ready">Ready</SelectItem>
                          <SelectItem value="shot">Shot</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      data-testid="submit-scene-button"
                      disabled={creating}
                    >
                      {creating ? 'Creating...' : 'Create Scene'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </header>

        {/* Active-filters summary banner */}
        {filtersActive && hasAnyScenes && (
          <div
            className="mb-6 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-border bg-card/60"
            data-testid="sb-filter-banner"
          >
            <p className="text-xs text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{totalFiltered}</span> of {scenes.length} scenes
              <span className="hidden sm:inline"> · {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} active</span>
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters(defaultFilters())}
              className="h-7 px-2 text-xs gap-1"
              data-testid="sb-filter-banner-clear"
            >
              <X className="size-3.5" />
              Clear
            </Button>
          </div>
        )}

        {/* Body */}
        {viewMode === 'schedule' ? (
          <ScheduleBoard filmId={filmId} canManage={canManage} onSceneClick={onSceneClick} />
        ) : !hasAnyScenes ? (
          <div
            className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center"
            data-testid="sb-empty-state"
          >
            <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
              <Plus className="size-6 text-primary" />
            </span>
            <h3 className="font-display text-2xl font-bold tracking-tight mb-1">No scenes yet</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-5">
              Add your first scene to start the breakdown.
            </p>
            {canManage && !scenesAtLimit && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="rounded-xl"
                data-testid="sb-empty-add-cta"
              >
                <Plus className="size-4 mr-1.5" /> Add Scene
              </Button>
            )}
          </div>
        ) : filtersActive && totalFiltered === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center"
            data-testid="sb-empty-filtered"
          >
            <span className="size-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
              <SlidersHorizontal className="size-6 text-muted-foreground" />
            </span>
            <h3 className="font-display text-2xl font-bold tracking-tight mb-1">No scenes match these filters</h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-5">
              Try widening Int/Ext, Time of Day, Location, or Due window.
            </p>
            <Button
              onClick={() => setFilters(defaultFilters())}
              variant="outline"
              className="rounded-xl"
              data-testid="sb-clear-filters-empty"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="sb-board">
            {COLUMNS.map((col) => {
              const colScenes = scenesByColumn[col.id] || [];
              const prefs = colPrefs[col.id] || { collapsed: false, sortBy: 'newest' };
              return (
                <Column
                  key={col.id}
                  column={col}
                  scenes={colScenes}
                  isDropTarget={dropTargetCol === col.id}
                  collapsed={prefs.collapsed}
                  sortBy={prefs.sortBy}
                  canManage={canManage}
                  onDragOver={onColumnDragOver(col.id)}
                  onDragLeave={onColumnDragLeave(col.id)}
                  onDrop={onColumnDrop(col.id)}
                  onCardDragStart={onCardDragStart}
                  onCardDragEnd={onCardDragEnd}
                  draggingId={draggingId}
                  onSceneClick={onSceneClick}
                  onToggleCollapse={() => toggleColumnCollapse(col.id)}
                  onSortChange={setColumnSort(col.id)}
                  onCopyList={copyColumnList(col.id)}
                  onBulkMoveTo={beginBulkMove(col.id)}
                />
              );
            })}
          </div>
        )}
      </main>

      {/* Bulk-move confirm dialog */}
      <Dialog
        open={!!bulkMove}
        onOpenChange={(open) => { if (!open && !bulkMoving) setBulkMove(null); }}
      >
        <DialogContent data-testid="sb-bulk-move-dialog">
          <DialogHeader>
            <DialogTitle>
              Move {bulkMove?.count || 0} {bulkMove?.count === 1 ? 'scene' : 'scenes'}?
            </DialogTitle>
            <DialogDescription>
              {bulkMove && (
                <>
                  All scenes currently in{' '}
                  <span className="font-semibold text-foreground">
                    {COLUMNS.find((c) => c.id === bulkMove.fromColId)?.label}
                  </span>{' '}
                  will be set to{' '}
                  <span className="font-semibold text-foreground">
                    {COLUMNS.find((c) => c.id === bulkMove.toColId)?.label}
                  </span>.
                  This can be reverted by moving them back individually or in bulk.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBulkMove(null)}
              disabled={bulkMoving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmBulkMove}
              disabled={bulkMoving}
              data-testid="sb-bulk-move-confirm"
            >
              <Check className="size-4 mr-1.5" />
              {bulkMoving ? 'Moving…' : 'Move all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SceneBoard;
